import { Logger } from "common/utility/logger/logger";
import { Strings } from "common/utility/string/string";
import { BehaviorSubject, Observable, Subject, interval, takeUntil } from "rxjs";
import { Environment } from "src/environments/environment";
import { ISocketRequest } from "../interfaces/ISocketRequest";
import { ConnectionState } from "./connection-state";
import { ReadyState } from "./ready-state";
import { SocketMethod } from "./socket-method";

export class Socket {
    private socket: WebSocket | undefined = undefined;
    private message: Subject<any> = new Subject();
    private unsubscribe: Subject<void> = new Subject();
    private failedConnectionAttempts: number = 0;
    private connected: boolean = false;
    private connectionState: BehaviorSubject<ConnectionState> = new BehaviorSubject<ConnectionState>(ConnectionState.Disconnected);
    private pingRequest: ISocketRequest = this.createRequest(SocketMethod.KeepAlive, null, {}, false)
    private pollRateMinutes: number = 5

    private get keepAlivePollRate(): number {
        return this.pollRateMinutes * 60 * 1000
    }

    public get Messages(): Observable<any> {
        return this.message;
    }

    public get FailedConnectionAttempts(): number {
        return this.failedConnectionAttempts;
    }

    public get Connected(): boolean {
        return this.connected;
    }

    public get ConnectionState(): BehaviorSubject<ConnectionState> {
        return this.connectionState;
    }

    public connect(url: string, token: string, provider: string): void {

        const request: string = this.buildRequest(url, new Map([
            ['token', token],
            ['provider', provider]
        ]))

        this.socket = new WebSocket(request);
        this.ConnectionState.next(ConnectionState.Connecting)

        this.socket.onopen = (_event) => {
            this.notifyState(this.socket)
        }

        this.socket.onclose = () => {
            this.notifyState(this.socket)
        }

        this.socket.onmessage = (event: MessageEvent<any>): any => {
            this.message.next(JSON.parse(event.data));
        }

        this.socket.onerror = (event: Event) => {
            Logger.error(`Websocket Connection Error. event: ${JSON.stringify(event)}`)
            this.failedConnectionAttempts++;
            this.notifyState(this.socket)
        }
    } // connect()

    public disconnect(): void {
        this.socket?.close();
    } // disconnect()

    public createRequest(action: SocketMethod, authId: string | null, payload: any, transactional: boolean = true): ISocketRequest {
        return {
            action: action,
            authenticationId: authId,
            payload: payload,
            transactionId: transactional ? Strings.newGuid() : undefined
        }
    } // createRequest()

    public sendMessage(message: any): void {
        if (this.Connected) {
            this.socket?.send(JSON.stringify(message))
        }
    } // sendMessage()

    public cleanUp(): void {
        this.unsubscribe.next()
        this.unsubscribe.complete();
    }

    constructor() {
        this.pollRateMinutes = Environment.SocketPingRateMinutes
        this.monitorConnectionState()
        this.sendKeepAlivePings()
    }

    private buildRequest(url: string, parameters: Map<string, string>): string {
        const returnValue: URL = new URL(url);
        parameters.forEach((value, key) => {
            returnValue.searchParams.append(key, value)
        });

        return returnValue.toString();
    }

    private monitorConnectionState(): void {
        this.connectionState.pipe(
            takeUntil(this.unsubscribe)
        ).subscribe(state => {
            this.connected = state === ConnectionState.Connected
        })
    }

    private sendKeepAlivePings(): void {
        interval(this.keepAlivePollRate).pipe(
            takeUntil(this.unsubscribe)
        ).subscribe(() => {
            if (this.connected) {
                this.sendMessage(this.pingRequest)
            }
        })
    }

    private notifyState(socket: WebSocket | undefined): void {
        let state: ConnectionState = ConnectionState.Disconnected

        switch (socket?.readyState) {
            case ReadyState.Connecting:
                state = ConnectionState.Connecting
                break

            case ReadyState.Open:
                state = ConnectionState.Connected
                break

            case ReadyState.Closing:
                state = ConnectionState.Disconnecting
                break

            case ReadyState.Closed:
                state = ConnectionState.Disconnected
                break

            default:
                state = ConnectionState.Disconnected
                break
        } // notifyState()

        this.ConnectionState.next(state)
    }
}
